import type { PracticeWay, TaskPractice } from '../../../../data-contract/position-alignment';
import { practiceFrequencySchema, taskPracticeSchema } from '../../../../data-contract/position-alignment';
import type { WorkTaskAutomationRecommendation } from '../../../../data-contract/task-management';

export const PRACTICE_ANSWERS = ['NOT_DOING', 'WITHOUT_AI', 'ASSISTANT', 'AGENT'] as const;
export type PracticeAnswer = (typeof PRACTICE_ANSWERS)[number];
export const PRACTICE_FREQUENCIES = practiceFrequencySchema.options;

const ANSWERS: Record<PracticeWay, PracticeAnswer> = {
    NOT_YET: 'NOT_DOING',
    BY_HAND: 'WITHOUT_AI',
    WITH_TOOLS: 'WITHOUT_AI',
    WITH_AI: 'ASSISTANT',
    WITH_AGENT: 'AGENT',
};
const WAYS: Record<PracticeAnswer, PracticeWay> = {
    NOT_DOING: 'NOT_YET',
    WITHOUT_AI: 'BY_HAND',
    ASSISTANT: 'WITH_AI',
    AGENT: 'WITH_AGENT',
};
export const answerOfWay = (way: PracticeWay | null | undefined): PracticeAnswer | null =>
    way == null ? null : ANSWERS[way];
export const wayOfAnswer = (answer: PracticeAnswer): PracticeWay => WAYS[answer];

export type MissingFollowUp =
    | 'answer'
    | 'matches_documentation'
    | 'differences'
    | 'description'
    | 'frequency'
    | 'tools'
    | 'uses_skills';
const doingFollowUps = (practice: TaskPractice, documentedStepCount: number): MissingFollowUp[] => {
    if (documentedStepCount === 0) return [];
    const missing: MissingFollowUp[] = [];
    if (practice.matches_documentation === null) missing.push('matches_documentation');
    if (practice.matches_documentation === false && !practice.differences.trim()) missing.push('differences');
    return missing;
};
const agentFollowUps = (practice: TaskPractice): MissingFollowUp[] =>
    practice.uses_skills === null ? ['uses_skills'] : [];
export const missingFollowUps = (
    practice: TaskPractice | null | undefined,
    documentedStepCount = 1,
): MissingFollowUp[] => {
    const answer = answerOfWay(practice?.way);
    if (answer === null || practice == null) return ['answer'];
    const missing: MissingFollowUp[] = answer === 'NOT_DOING' ? [] : doingFollowUps(practice, documentedStepCount);
    if (!practice.description.trim()) missing.push('description');
    if (answer === 'NOT_DOING') return missing;
    if (practice.frequency === null) missing.push('frequency');
    if ((answer === 'ASSISTANT' || answer === 'AGENT') && !practice.tools.some((tool) => tool.trim()))
        missing.push('tools');
    if (answer === 'AGENT') missing.push(...agentFollowUps(practice));
    return missing;
};
export const isAnswered = (practice: TaskPractice | null | undefined, documentedStepCount = 1): boolean =>
    missingFollowUps(practice, documentedStepCount).length === 0;

export const potentialShare = (recommendation: WorkTaskAutomationRecommendation | null | undefined): number | null =>
    recommendation?.status === 'succeeded' ? (recommendation.automatablePercentage ?? null) : null;

export const actualShare = (
    answer: PracticeAnswer | null,
    recommendation: WorkTaskAutomationRecommendation | null | undefined,
): number | null => {
    if (answer === null || answer === 'NOT_DOING') return null;
    if (answer === 'WITHOUT_AI') return 0;
    if (recommendation?.status !== 'succeeded') return null;
    const potential = potentialShare(recommendation);
    if (potential === null) return null;
    // Only automations of the chosen level count; a level without its own recommendation has no Actual rather than
    // borrowing the other level's share.
    const shares = recommendation.automations.flatMap(({ autonomyLevel, automatablePercentage }) =>
        autonomyLevel !== undefined &&
        automatablePercentage !== undefined &&
        (answer === 'ASSISTANT' ? autonomyLevel <= 2 : autonomyLevel >= 3)
            ? [automatablePercentage]
            : [],
    );
    return shares.length ? Math.round(Math.min(potential, Math.max(...shares))) : null;
};

/** Stored answer-time Actual wins; old answers without one may use the current recommendation. */
export const displayedActual = (
    practice: TaskPractice | null | undefined,
    recommendation: WorkTaskAutomationRecommendation | null | undefined,
    documentedStepCount = 1,
): number | null => {
    if (!practice || !isAnswered(practice, documentedStepCount)) return null;
    const answer = answerOfWay(practice.way);
    if (answer === 'NOT_DOING') return null;
    if (answer === 'WITHOUT_AI') return 0;
    return practice.automation_share ?? actualShare(answer, recommendation);
};

/** The answers that belong to one way: switching ways parks them instead of carrying them over. */
export type WayAnswers = Pick<TaskPractice, 'description' | 'tools' | 'uses_skills'>;

const EMPTY_WAY_ANSWERS: WayAnswers = { description: '', tools: [], uses_skills: null };

const wayAnswersOf = ({ description, tools, uses_skills }: TaskPractice): WayAnswers => ({
    description,
    tools,
    uses_skills,
});

/**
 * Applies a change of way: the old way's answers are parked in `parked` and the new way's come back (empty the first
 * time), so text written for one way never answers another. Returns the practice unchanged when the way stays.
 */
export const switchWayAnswers = (
    practice: TaskPractice,
    way: PracticeWay | null | undefined,
    parked: Partial<Record<PracticeAnswer, WayAnswers>>,
): TaskPractice => {
    const from = answerOfWay(practice.way);
    const to = way === undefined ? from : answerOfWay(way);
    if (from === to) return practice;
    if (from !== null) parked[from] = wayAnswersOf(practice);
    return { ...practice, ...((to === null ? undefined : parked[to]) ?? EMPTY_WAY_ANSWERS) };
};

export const emptyPractice = (): TaskPractice =>
    taskPracticeSchema.parse({ way: null, automation_share: null, description: '' });
